import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import Checkbox from "components/common/Checkbox";
import { useTheme } from "@mui/material/styles";
import {
  DatePicker,
  DesktopTimePicker,
  PickersActionBarProps,
} from "@mui/x-date-pickers";
import SimpleRichEditor from "../editor/SimpleRichEditor";
import { prependRichContent } from "../editor/utils";
import { useState, useEffect } from "react";
import {
  DEFAULT_EVENT_DURATION_MS,
  EphemeralEvent,
  FormErrors,
} from "../../types/calendars";
import FamilySelect from "../creation/FamilySelect";
import { AutocompleteEntry } from "../email/AddressCompletion";
import { RecurringUnit, EventReminder } from "protogen/calendar_pb";
import AddressInputList from "../email/AddressInputList";
import useIsMobile from "../hooks/useIsMobile";
import TimezoneDropdown from "../common/TimezoneDropdown";
import AddressAutocomplete from "components/common/AddressAutocomplete";
import RecurringEventCheckbox from "./RecurringEventCheckbox";
import { ChevronDown, Clock, CalendarDays, Info } from "lucide-react";
import VideoCallSetup from "./VideoCallSetup";
import Select from "components/common/Select";
import { useGetFamilyDetails } from "services/advisor";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import ReminderInput from "./ReminderInput";
import {
  useGetAdvisorCalendarIntegrations,
  useGetMemberCalendarIntegrations,
  useGetFamilyCalendarIntegrations,
} from "services/calendar";
import { CalendarIntegration } from "protogen/calendar_service_pb";
import CustomRecurrence, { RecurrenceData } from "./CustomRecurrence";

const transparencyOptions: Record<string, string> = {
  Busy: "opaque",
  Free: "transparent",
};

const visibilityOptions: Record<string, string> = {
  "Default visibility": "default",
  Public: "public",
  Private: "private",
};

const isSameDay = (d1: Date, d2: Date) => {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const EventDateActionBar = ({ onClear }: PickersActionBarProps) => {
  return (
    <Box
      sx={{ alignSelf: "flex-end", marginRight: "12px", marginTop: "-50px" }}
    >
      <Button onClick={onClear} variant="text">
        Clear
      </Button>
    </Box>
  );
};

const EventDate = ({
  label,
  disabled,
  value,
  update,
  error,
  format,
  placeholder,
  forceShrink,
  clearable,
  onOpen,
}: {
  label: string;
  disabled: boolean;
  value?: Date;
  update: (d: Date | null) => void;
  error?: string | null;
  format?: string | null;
  placeholder?: string;
  forceShrink?: boolean;
  clearable?: boolean;
  onOpen?: () => void;
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  return (
    <FormControl sx={{ width: "100%" }}>
      <DatePicker
        disabled={disabled}
        label={label}
        value={value}
        sx={{ width: "100%", ".MuiButtonBase-root": { marginRight: "unset" } }}
        onChange={update}
        format={format ? format : "MM/dd/yyyy"}
        open={dateOpen}
        onClose={() => setDateOpen(false)}
        slots={
          clearable
            ? {
                actionBar: EventDateActionBar,
              }
            : {}
        }
        slotProps={{
          textField: {
            error: !!error,
            onClick: (e) => {
              e.stopPropagation();
              setDateOpen(true);
              // Call onOpen callback if provided
              if (onOpen) {
                onOpen();
              }
              // @kegami - We don't control the datepicker render and focus.
              // We assume it's async and runs off useState.  We re-focus the input after the event loop.
              setTimeout(() => {
                (e.target as HTMLInputElement).focus();
              });
            },
            InputLabelProps: { ...(forceShrink ? { shrink: true } : {}) },
            placeholder:
              value === undefined && placeholder ? placeholder : undefined,
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      setDateOpen(true);
                      if (onOpen) {
                        onOpen();
                      }
                    }}
                    edge="end"
                  >
                    <CalendarDays size={20} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
          ...(clearable
            ? {
                actionBar: {
                  actions: ["clear"],
                },
                popper: {
                  // Popper is for desktop - change from grid to flex
                  sx: {
                    "& .MuiPickersLayout-root": {
                      display: "flex",
                      flexDirection: "column",
                    },
                  },
                },
                dialog: {
                  // dialog is for mobile - change grid to flex
                  sx: {
                    "& .MuiPickersLayout-root": {
                      display: "flex",
                      flexDirection: "column",
                    },
                  },
                },
              }
            : {}),
        }}
      />
    </FormControl>
  );
};

const EventTime = ({
  value,
  update,
  error,
  sx,
}: {
  value?: Date;
  update: (d: Date | null) => void;
  error?: string | null;
  sx?: React.CSSProperties;
}) => {
  const [timeOpen, setTimeOpen] = useState(false);
  return (
    <DesktopTimePicker
      value={value}
      sx={{
        ".MuiButtonBase-root": { marginRight: "unset" },
        "& .MuiInputBase-input": { marginTop: "0px" },
        ...sx,
      }}
      onChange={update}
      open={timeOpen}
      onClose={() => setTimeOpen(false)}
      slotProps={{
        textField: {
          error: !!error,
          onClick: (e) => {
            e.stopPropagation();
            setTimeOpen(!timeOpen);
            // @kegami - We don't control the datepicker render and focus.
            // We assume it's async and runs off useState.  We re-focus the input after the event loop.
            setTimeout(() => {
              (e.target as HTMLInputElement).focus();
            });
          },

          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setTimeOpen(!timeOpen)} edge="end">
                  <Clock size={20} />
                </IconButton>
              </InputAdornment>
            ),
          },
        },
      }}
    />
  );
};
interface SectionProps {
  loading: boolean;
  errors: FormErrors | null;
  formData: EphemeralEvent;
  updateFormData: (f: EphemeralEvent) => void;
  sectionDisabled?: boolean;
}
const EventRange = ({
  loading,
  errors,
  formData,
  updateFormData,
}: SectionProps) => {
  if (formData.allDay) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "15px",
          alignItems: "center",
        }}
      >
        <EventDate
          disabled={loading}
          value={formData.startDate}
          label="Starts"
          error={errors?.startDate}
          format="EEE, MMM d"
          update={(v: Date | null) => {
            let updates: Partial<EphemeralEvent> = {
              startDate: v ? new Date(v) : undefined,
            };
            // For all-day events, always set end date to start date if end date is not set
            if (v && !formData.endDate) {
              updates.endDate = new Date(v);
            }
            // Auto update the enddate for the user if start date is past or equal to end date
            else if (v && formData.endDate && v >= formData.endDate) {
              updates.endDate = v ? new Date(v) : undefined;
            }
            if (v && formData.endDate && !isSameDay(v, formData.endDate)) {
              updates.recurringUnit = undefined;
              updates.recurringInterval = undefined;
              updates.isRecurring = false;
            }
            updateFormData(updates);
          }}
        />
        <Typography variant="h3" sx={{ color: "text.tertiary" }}>
          -
        </Typography>
        <EventDate
          disabled={loading}
          label="Ends"
          value={formData.endDate}
          error={errors?.endDate}
          format="EEE, MMM d"
          placeholder="End date"
          onOpen={() => {
            // When the end date picker opens, auto-set it to start date if not already set
            if (!formData.endDate && formData.startDate) {
              updateFormData({ endDate: new Date(formData.startDate) });
            }
          }}
          update={(v: Date | null) => {
            let updates: Partial<EphemeralEvent> = {
              endDate: v ? new Date(v) : undefined,
            };
            // Auto update the start date for the user if end date is before start date
            if (v && formData.endDate && v < formData.endDate) {
              updates.startDate = v ? new Date(v) : undefined;
            }
            if (v && formData.startDate && !isSameDay(v, formData.startDate)) {
              updates.recurringUnit = undefined;
              updates.recurringInterval = undefined;
              updates.isRecurring = false;
            }
            updateFormData(updates);
          }}
        />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "15px",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          width: "100%",
        }}
      >
        <FormControl sx={{ width: "100%" }}>
          <EventDate
            value={formData.startDate}
            disabled={loading}
            label="Starts"
            update={(v: Date | null) =>
              updateFormData({
                startDate: v || undefined,
              })
            }
            format="EEE, MMM d"
            error={errors?.startDate}
          />
        </FormControl>
        <EventTime
          value={formData.startDate}
          sx={{ width: "100%" }}
          update={(v: Date | null) => {
            // Use the default if we don't have start/end, or their difference is 0.
            const interval =
              (formData.startDate &&
                formData.endDate &&
                formData.endDate.getTime() - formData.startDate.getTime()) ||
              DEFAULT_EVENT_DURATION_MS;
            if (v) {
              updateFormData({
                startDate: v,
                // Add 1 hour to the end date
                endDate: new Date(v.getTime() + interval),
              });
            } else {
              updateFormData({ startDate: v || undefined });
            }
          }}
          error={errors?.startDate}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          width: "100%",
        }}
      >
        <FormControl sx={{ width: "100%" }}>
          <EventDate
            value={formData.endDate}
            disabled={loading}
            label="Ends"
            update={(v: Date | null) =>
              updateFormData({
                endDate: v || undefined,
              })
            }
            format="EEE, MMM d"
            error={errors?.startDate}
          />
        </FormControl>
        <EventTime
          value={formData.endDate}
          sx={{ width: "100%" }}
          update={(v: Date | null) =>
            updateFormData({ endDate: v || undefined })
          }
          error={errors?.endDate}
        />
      </Box>
    </Box>
  );
};

const LabeledOptionSelect = ({
  label,
  options,
  value,
  loading,
  onChange,
}: {
  label: string;
  options: Record<string, string>;
  value: string | undefined;
  loading: boolean;
  onChange: (value: string) => void;
}) => {
  const selectedLabel =
    Object.keys(options).find((key) => options[key] === value) ||
    Object.keys(options)[0];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Select
        variant="detachedOutlined"
        size="small"
        label={label}
        disabled={loading}
        value={selectedLabel}
        renderValue={(value) => {
          return value;
        }}
        onChange={(e) => onChange(options[e.target.value as string])}
      >
        {Object.keys(options).map((k) => (
          <MenuItem value={k} key={k}>
            {k}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const ReminderSection = ({
  errors,
  formData,
  updateFormData,
}: SectionProps) => {
  return (
    <Box>
      {formData?.reminders &&
        formData.reminders.map((r, i) => {
          return (
            <ReminderInput
              key={i}
              reminder={r}
              index={i}
              onUpdate={(index, reminder) => {
                const reminders = formData.reminders || [];
                reminders[index] = reminder;
                updateFormData({ reminders });
              }}
              onDelete={(index) => {
                const reminders = formData.reminders || [];
                reminders.splice(index, 1);
                updateFormData({ reminders });
              }}
            />
          );
        })}
      <Button
        variant="text"
        onClick={() => {
          updateFormData({
            reminders: [
              ...(formData?.reminders || []),
              new EventReminder({ value: 5, unit: "minutes" }),
            ],
          });
        }}
      >
        Add a reminder
      </Button>
      {!!errors?.reminders && (
        <Typography color="secondary.dark">{errors.reminders}</Typography>
      )}
    </Box>
  );
};

const CalendarSelector = ({
  formData,
  updateFormData,
  twoWaySyncIntegrations,
}: {
  formData: EphemeralEvent;
  updateFormData: (f: EphemeralEvent) => void;
  twoWaySyncIntegrations: CalendarIntegration[];
}) => {
  const isMobile = useIsMobile();
  const calendarOptions: Array<{
    label: string;
    value: string;
    integrationRef?: string;
    calendarId?: string;
  }> = [
    { label: "Faye Calendar", value: "faye" },
    ...twoWaySyncIntegrations.flatMap((integration: CalendarIntegration) =>
      integration.calendars
        .filter((cal) => cal.enabled && cal.role !== "reader")
        .map((cal) => ({
          label: `${integration.name} — ${cal.summary}`,
          value: `${integration.ref}:${cal.id}`,
          integrationRef: integration.ref,
          calendarId: cal.id,
        })),
    ),
  ];

  const selectedCalendarValue =
    formData.targetIntegrationRef && formData.targetCalendarId
      ? `${formData.targetIntegrationRef}:${formData.targetCalendarId}`
      : "faye";

  if (calendarOptions.length <= 1) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Typography variant={isMobile ? "h4" : "bodyHeavy"}>Calendar</Typography>
      <FormControl sx={{ width: "100%" }}>
        <Select
          variant="detachedOutlined"
          size="small"
          value={selectedCalendarValue}
          onChange={(e) => {
            const selectedOption = calendarOptions.find(
              (opt) => opt.value === (e.target.value as string),
            );
            if (selectedOption?.value === "faye") {
              updateFormData({
                targetIntegrationRef: undefined,
                targetCalendarId: undefined,
              });
            } else if (selectedOption) {
              updateFormData({
                targetIntegrationRef: selectedOption.integrationRef,
                targetCalendarId: selectedOption.calendarId,
                reminders: undefined,
              });
            }
          }}
          IconComponent={ChevronDown}
        >
          {calendarOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedCalendarValue !== "faye" && (
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.notifyCalendarOwner ?? true}
              onChange={(e) =>
                updateFormData({ notifyCalendarOwner: e.target.checked })
              }
              size="small"
            />
          }
          label="Notify calendar owner by email"
        />
      )}
    </Box>
  );
};

interface Props {
  formData: EphemeralEvent;
  updateFormData: (f: EphemeralEvent) => void;
  errors: FormErrors | null;
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
  selectFamily?: boolean;
  hideAction?: boolean;
  actionName?: string;
  attendeeAutocomplete?: AutocompleteEntry[];
  accountType?: "advisor" | "member";
  applyDefaultCalendar?: boolean;
}

export default ({
  loading,
  errors,
  formData,
  updateFormData,
  onSubmit,
  selectFamily = false,
  hideAction = false,
  actionName = "Save event",
  attendeeAutocomplete,
  accountType = "advisor",
  applyDefaultCalendar = false,
}: Props) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [isRecurringSingleInstance, setIsRecurringSingleInstance] =
    useState(true);
  const isRecurringEvent =
    formData?.recurringUnit !== 0 && formData?.recurringInterval;

  let initialDetailsText: string | undefined = undefined;
  if (formData?.description?.json) {
    initialDetailsText = JSON.parse(formData?.description?.json || "{}");
  } else if (formData?.description?.html) {
    initialDetailsText = formData?.description?.html;
  } else if (formData?.initialDetailsText) {
    initialDetailsText = formData?.initialDetailsText;
  }

  const [hover, setHover] = useState(false);
  const [moreOptionsExpanded, setMoreOptionsExpanded] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const { data: familyDetails, request: getFamilyDetails } =
    useGetFamilyDetails();
  const { data: memberDetails, refresh: getMemberDetails } = useMemberDetails();

  const getIntegrationsFn =
    accountType === "advisor"
      ? useGetAdvisorCalendarIntegrations
      : useGetMemberCalendarIntegrations;
  const { data: integrationsData, request: getIntegrations } =
    getIntegrationsFn();

  const { data: familyIntegrationsData, request: getFamilyIntegrations } =
    useGetFamilyCalendarIntegrations();

  useEffect(() => {
    if (formData.familyRef && accountType === "advisor") {
      getFamilyDetails({ familyRef: formData.familyRef });
      getFamilyIntegrations({ familyRef: formData.familyRef });
    }
  }, [formData.familyRef]);

  useEffect(() => {
    if (accountType === "member") {
      getIntegrations();
      getMemberDetails();
    } else if (accountType === "advisor") {
      getIntegrations();
    }
  }, []);

  const family =
    accountType === "advisor" ? familyDetails?.family : memberDetails?.family;
  const defaultCalendarIntegrationRef = family?.defaultCalendarIntegrationRef;
  const defaultCalendarId = family?.defaultCalendarId;

  // Set default calendar from family settings when loaded (only for new events)
  useEffect(() => {
    if (
      applyDefaultCalendar &&
      defaultCalendarIntegrationRef &&
      defaultCalendarId &&
      !formData.targetIntegrationRef &&
      !formData.targetCalendarId
    ) {
      updateFormData({
        targetIntegrationRef: defaultCalendarIntegrationRef,
        targetCalendarId: defaultCalendarId,
      });
    }
  }, [applyDefaultCalendar, defaultCalendarIntegrationRef, defaultCalendarId]);

  const handleInviteFamilyMembers = () => {
    if (accountType !== "advisor" || !familyDetails?.family?.familyMembers) {
      return;
    }

    const familyMembersToAdd = familyDetails.family.familyMembers
      .map((member: any) => member.primaryEmail || "")
      .filter((email: string) => email && email.includes("@"));

    if (familyMembersToAdd.length > 0) {
      const currentInvitees = formData.invitees || [];
      const currentEmails = new Set(
        currentInvitees.map((invitee: string) => invitee.toLowerCase()),
      );

      const newInvitees = familyMembersToAdd.filter(
        (email: string) => !currentEmails.has(email.toLowerCase()),
      );

      if (newInvitees.length > 0) {
        updateFormData({
          invitees: [...currentInvitees, ...newInvitees],
        });
      }
    }
  };

  const twoWaySyncIntegrations = (() => {
    // For advisors creating family events, use family's integrations
    // For members, use their own integrations
    const sourceData =
      accountType === "advisor" && formData.familyRef
        ? familyIntegrationsData
        : integrationsData;

    return (
      sourceData?.importIntegrations?.filter(
        (integration: CalendarIntegration) =>
          integration.accessScope === "read_write" &&
          integration.status === "active",
      ) || []
    );
  })();

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: isMobile ? "column" : undefined,
        gap: isMobile ? "24px" : "48px",
        height: "100%",
        paddingTop: "8px",
        ...(hover ? { backgroundColor: "#e8f4f8" } : {}),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          disabled={loading}
          label="Event name"
          fullWidth
          error={!!errors?.title}
          value={formData.title || ""}
          onChange={(e) => updateFormData({ title: e.target.value })}
          autoComplete="off"
          sx={{ margin: "12px 0px" }}
        />
        <SimpleRichEditor
          key={editorKey}
          initialContent={initialDetailsText}
          initialAttachments={formData?.initialAttachments || []}
          disabled={loading}
          placeholder="Description"
          setContent={(c) => {
            updateFormData({ description: c });
          }}
          setDragState={setHover}
          inputSx={{
            borderRadius: "12px",
            border: "1px solid #D4D4D4",
            boxShadow: "unset",
          }}
        />
        <Box sx={{ marginTop: "12px" }}>
          <AddressAutocomplete
            restrictToAddresses={false}
            initialValue={
              formData.location
                ? (() => {
                    try {
                      const loc = JSON.parse(formData.location || "");
                      if (loc.name && loc.formattedAddress) {
                        return `${loc.name} — ${loc.formattedAddress}`;
                      }
                      return loc.name || loc.formattedAddress;
                    } catch {
                      return formData.location;
                    }
                  })()
                : ""
            }
            setValue={(location) => {
              updateFormData({
                location: JSON.stringify(location),
              });
            }}
            label={"Location"}
            error={errors?.location}
            handleCopy={!!formData.location}
          />
        </Box>
        <Box sx={{ margin: "14px 0px" }}>
          <VideoCallSetup
            videoRoom={formData.videoRoom}
            onVideoRoomChange={(videoRoom) => {
              updateFormData({ videoRoom });
              if (videoRoom && videoRoom.uuid) {
                const videoCallLink = `${window.location.origin}/video/${videoRoom.uuid}`;
                const videoCallText = `Join video call: ${videoCallLink}`;

                const newDescription = prependRichContent(
                  videoCallText,
                  formData?.description,
                );
                updateFormData({
                  description: newDescription,
                  initialDetailsText: newDescription.text,
                });
                // Force the editor to re-render with the new content
                // We only pass in initial content to our tiptap editors
                setEditorKey((prev) => prev + 1);
              }
            }}
            loading={loading}
            familyRef={formData.familyRef}
          />
        </Box>
        <Box sx={{ margin: "14px 0px" }}>
          <ButtonGroup fullWidth>
            <Button
              sx={{
                marginRight: "10px",
              }}
              variant={formData.allDay ? "buttonGroupSelected" : "buttonGroup"}
              onClick={() => {
                const shouldClearTimezone =
                  !formData.recurringByDay ||
                  formData.recurringByDay.length === 0;
                updateFormData({
                  allDay: true,
                  displayTimezone: shouldClearTimezone
                    ? ""
                    : formData.displayTimezone,
                });
              }}
            >
              All day
            </Button>
            <Button
              variant={!formData.allDay ? "buttonGroupSelected" : "buttonGroup"}
              onClick={() => updateFormData({ allDay: false })}
            >
              Specific time
            </Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ marginBottom: "14px" }}>
          <EventRange
            loading={loading}
            errors={errors}
            formData={formData}
            updateFormData={updateFormData}
          />
        </Box>
        {errors && errors["displayTimezone"] && (
          <Typography color="secondary.dark">
            {errors["displayTimezone"]}
          </Typography>
        )}
        <Box sx={{ marginTop: "14px" }}>
          <CustomRecurrence
            startDate={formData.startDate}
            initialData={
              formData.isRecurring &&
              formData.recurringUnit !== undefined &&
              formData.recurringInterval !== undefined
                ? (() => {
                    const reverseUnitMap: Record<
                      RecurringUnit,
                      RecurrenceData["unit"]
                    > = {
                      [RecurringUnit.RecurringUnit_UNSPECIFIED]: "day",
                      [RecurringUnit.RecurringUnit_DAY]: "day",
                      [RecurringUnit.RecurringUnit_WEEK]: "week",
                      [RecurringUnit.RecurringUnit_MONTH]: "month",
                      [RecurringUnit.RecurringUnit_YEAR]: "year",
                    };

                    // Determine monthly pattern from stored data
                    let monthlyPattern: "dayOfMonth" | "dayOfWeek" | undefined;
                    if (reverseUnitMap[formData.recurringUnit] === "month") {
                      if (
                        formData.recurringBySetPos &&
                        formData.recurringBySetPos > 0
                      ) {
                        monthlyPattern = "dayOfWeek";
                      } else if (
                        formData.recurringByMonthDay &&
                        formData.recurringByMonthDay.length > 0
                      ) {
                        monthlyPattern = "dayOfMonth";
                      }
                    }

                    return {
                      interval: formData.recurringInterval,
                      unit: reverseUnitMap[formData.recurringUnit],
                      byDay: formData.recurringByDay || [],
                      monthlyPattern,
                      endType:
                        formData.recurringCount && formData.recurringCount > 0
                          ? "after"
                          : formData.recurringUntil
                            ? "on"
                            : "never",
                      endDate: formData.recurringUntil,
                      count: formData.recurringCount || undefined,
                    };
                  })()
                : undefined
            }
            onChange={(data: RecurrenceData | null) => {
              if (!data) {
                updateFormData({
                  recurringUnit: undefined,
                  recurringInterval: undefined,
                  isRecurring: false,
                  recurringUntil: undefined,
                  recurringByDay: [],
                  recurringCount: 0,
                  recurringByMonthDay: [],
                  recurringBySetPos: 0,
                });
              } else {
                const unitMap: Record<RecurrenceData["unit"], RecurringUnit> = {
                  day: RecurringUnit.RecurringUnit_DAY,
                  week: RecurringUnit.RecurringUnit_WEEK,
                  month: RecurringUnit.RecurringUnit_MONTH,
                  year: RecurringUnit.RecurringUnit_YEAR,
                };

                // Calculate monthly pattern fields
                let recurringByMonthDay: number[] = [];
                let recurringBySetPos = 0;
                let recurringByDay: number[] = [];

                if (
                  data.unit === "month" &&
                  data.monthlyPattern &&
                  formData.startDate
                ) {
                  if (data.monthlyPattern === "dayOfMonth") {
                    recurringByMonthDay = [formData.startDate.getDate()];
                  } else if (data.monthlyPattern === "dayOfWeek") {
                    const dayOfMonth = formData.startDate.getDate();
                    const occurrence = Math.ceil(dayOfMonth / 7);
                    recurringBySetPos = occurrence;
                    // Explicitly set the weekday to avoid timezone issues
                    // getDay() returns 0-6 for Sun-Sat, which matches our backend format
                    recurringByDay = [formData.startDate.getDay()];
                  }
                } else if (data.unit === "week") {
                  // For weekly recurrence, use the selected days
                  recurringByDay = data.byDay;
                }

                const needsTimezone = recurringByDay.length > 0;
                const displayTimezone = needsTimezone
                  ? formData.displayTimezone ||
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  : formData.displayTimezone || "";

                updateFormData({
                  recurringUnit: unitMap[data.unit],
                  recurringInterval: data.interval,
                  isRecurring: true,
                  recurringUntil:
                    data.endType === "on" && data.endDate
                      ? (() => {
                          const endOfDay = new Date(data.endDate);
                          endOfDay.setHours(23, 59, 59, 999);
                          return endOfDay;
                        })()
                      : undefined,
                  recurringByDay,
                  recurringCount:
                    data.endType === "after" && data.count ? data.count : 0,
                  recurringByMonthDay,
                  recurringBySetPos,
                  displayTimezone,
                });
              }
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          width: "329px",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Box display="flex" flexDirection="column" gap="24px">
          {selectFamily && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
                Family
              </Typography>
              <FamilySelect
                selectedFamily={formData.familyRef || null}
                onChange={(v) => updateFormData({ familyRef: v || undefined })}
                error={errors?.familyRef}
                disabled={loading}
              />
            </Box>
          )}

          <CalendarSelector
            formData={formData}
            updateFormData={updateFormData}
            twoWaySyncIntegrations={twoWaySyncIntegrations}
          />

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
              Attendees
            </Typography>
            {accountType === "advisor" && (
              <>
                <FormControl component="fieldset" style={{ width: "100%" }}>
                  <AddressInputList
                    recipients={formData.invitees || []}
                    setRecipients={(r) => updateFormData({ invitees: r })}
                    autocompleteEntries={attendeeAutocomplete}
                    placeholder={"Attendee email"}
                    fullWidthInput={true}
                    sx={{
                      minHeight: "60px",
                      border: `1px solid ${theme.palette.border}`,
                      borderRadius: "8px",
                      padding: "9px 20px",
                      gap: "6px",
                    }}
                  />
                </FormControl>
                {(formData.familyRef ||
                  (selectFamily && familyDetails?.family)) && (
                  <Button
                    variant="text"
                    onClick={handleInviteFamilyMembers}
                    sx={{
                      alignSelf: "flex-start",
                      textTransform: "none",
                      color: "primary.main",
                      padding: "4px 8px",
                    }}
                  >
                    + Invite family members
                  </Button>
                )}
              </>
            )}
            {accountType === "member" && (
              <FormControl component="fieldset" style={{ width: "100%" }}>
                <AddressInputList
                  recipients={formData.invitees || []}
                  setRecipients={(r) => updateFormData({ invitees: r })}
                  autocompleteEntries={attendeeAutocomplete || []}
                  placeholder={"Attendee email"}
                  fullWidthInput={true}
                  sx={{
                    minHeight: "60px",
                    border: `1px solid ${theme.palette.border}`,
                    borderRadius: "8px",
                    padding: "9px 20px",
                    gap: "6px",
                  }}
                />
              </FormControl>
            )}
          </Box>
        </Box>

        {accountType === "advisor" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "12px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
                Reminders
              </Typography>
              <Tooltip
                placement="right"
                title="Reminders are only sent to Advisors."
              >
                <Info style={{ cursor: "pointer" }} size={16} />
              </Tooltip>
            </Box>
            <ReminderSection
              loading={loading}
              errors={errors}
              formData={formData}
              updateFormData={updateFormData}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "22px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => setMoreOptionsExpanded(!moreOptionsExpanded)}
          >
            <Typography variant={isMobile ? "h4" : "bodyHeavy"}>
              More options
            </Typography>
            <ChevronDown
              size={16}
              style={{
                transform: moreOptionsExpanded
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </Box>
          {moreOptionsExpanded && (
            <>
              <Box>
                <LabeledOptionSelect
                  label="Availability"
                  options={transparencyOptions}
                  value={formData.transparency}
                  loading={loading}
                  onChange={(transparency) => updateFormData({ transparency })}
                />
              </Box>
              <Box>
                <LabeledOptionSelect
                  label="Visibility"
                  options={visibilityOptions}
                  value={formData.visibility}
                  loading={loading}
                  onChange={(visibility) => updateFormData({ visibility })}
                />
              </Box>
              {!formData.allDay && accountType === "advisor" && (
                <Box>
                  <TimezoneDropdown
                    size="small"
                    timezone={formData.displayTimezone || ""}
                    setTimezone={(timezone) => {
                      updateFormData({ displayTimezone: timezone });
                    }}
                    sx={{ width: "100%" }}
                    placeholder={"Event timezone (optional)"}
                    isSelectionClearable={true}
                  />
                  <FormHelperText
                    sx={{
                      padding: "0px 5px",
                      fontSize: "12px",
                      color: "#6B6E7B",
                    }}
                  >
                    This changes the timezone in which the event is created. It
                    will be displayed in the viewer's local time.
                  </FormHelperText>
                </Box>
              )}
            </>
          )}
        </Box>

        {hideAction && isRecurringEvent && actionName !== "Save event" && (
          <Box>
            <RecurringEventCheckbox
              actionLabel={"Edit"}
              isRecurringSingleInstance={isRecurringSingleInstance}
              onToggle={() => {
                setIsRecurringSingleInstance(!isRecurringSingleInstance);
                updateFormData({
                  isRecurringSingleInstance: !isRecurringSingleInstance,
                });
              }}
            />
          </Box>
        )}
        {!hideAction && (
          <Box sx={{ display: "flex", alignSelf: "end", height: "100%" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignSelf: "end",
              }}
            >
              <Button
                sx={{ alignSelf: "flex-end" }}
                onClick={() => {
                  onSubmit();
                }}
              >
                {actionName}
              </Button>
              {isRecurringEvent && actionName !== "Save event" && (
                <Box sx={{ alignSelf: "flex-end" }}>
                  <RecurringEventCheckbox
                    actionLabel={"Edit"}
                    isRecurringSingleInstance={isRecurringSingleInstance}
                    onToggle={() => {
                      setIsRecurringSingleInstance(!isRecurringSingleInstance);
                      updateFormData({
                        isRecurringSingleInstance: !isRecurringSingleInstance,
                      });
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
